import { useMutation, useQueryClient } from 'react-query'
import { useSnackbar } from 'notistack'
import { deleteCustomerGroup } from 'services/cm-api/delete-customer-group'

/**
 * Wraps `DELETE /customer-group/:id`. The delete service unwraps the
 * CMAPI's 409 `CustomerGroupInUse` body — we look at the resolved
 * value here, render the referencing banner IDs into a snackbar, and
 * skip the list invalidation. Genuine errors (404, 500, network)
 * still flow through `onError`.
 */
export function useDeleteCustomerGroup() {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(deleteCustomerGroup, {
    onSuccess: async (response) => {
      if (response.success === true) {
        enqueueSnackbar('Customer group deleted', { variant: 'success' })
        await queryClient.invalidateQueries(['getCustomerGroups'])
        return
      }

      // 409 — the group is still referenced. Surface the banner IDs
      // so the operator knows what to unhook first. The service
      // layer only returns the in-use error shape on this branch.
      const ids = response.error.referencing_banner_ids.join(', ')
      enqueueSnackbar(`Cannot delete — referenced by banner(s): ${ids}`, {
        variant: 'warning',
        persist: true,
      })
    },
    onError: (err: unknown) => {
      const axiosErr = err as {
        response?: { data?: { error?: { message?: string } } }
      }
      const apiMessage = axiosErr?.response?.data?.error?.message
      enqueueSnackbar(apiMessage ?? 'Failed to delete customer group', {
        variant: 'error',
      })
      console.error('deleteCustomerGroup failed: ', err)
    },
  })
}
